// src/stores/modelNodeStore.ts (Fixed)
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { enableMapSet } from "immer";
import { ModelNodeInterface } from "@/interfaces/uiNodeInterfaces/ModelNodeInterface";
import { Edge } from "@xyflow/react";
import { ModelPropertyInterface, ModelPropertyType, ModelInterface } from "@/interfaces";
import { buildEdgeFromIds } from "@/utils/flow/buildEdge";
import PROFESSIONAL_BACKGROUND_COLORS from "@/constants/ProfessionalColors";
import { ensureModelPositions } from "@/utils/flow/flowUtils";

enableMapSet();

interface ModelNodeStoreState {
  modelNodes: ModelNodeInterface[];
  edges: Edge[];
  modelNodeColors: Record<string, string>;
  modelNodeById: Map<string, ModelNodeInterface>;
  modelNodeByName: Map<string, ModelNodeInterface>;
  edgeById: Map<string, Edge>;
  propertiesByModelNode: Map<string, Set<string>>;

  // ModelNodeInterface Operations
  addModelNode: (
    name?: string,
    theme?: string,
    position?: { x: number; y: number }
  ) => ModelNodeInterface;
  removeModelNode: (modelNodeId: string) => void;
  updateModelNodeName: (modelNodeId: string, name: string) => { success: boolean; error?: string };
  clearModelNodes: () => void;
  setModelNodeExpanded: (modelNodeId: string, isExpanded: boolean) => boolean;

  // ModelPropertyInterface Operations
  addModelProperty: (
    modelNodeId: string,
    property: ModelPropertyInterface,
    createEdge?: boolean
  ) => boolean;
  removeModelProperty: (modelNodeId: string, propertyName: string) => boolean;
  updateProperty: (
    modelNodeId: string,
    propertyName: string,
    updates: Partial<ModelPropertyInterface>
  ) => boolean;
  updatePropertyType: (
    modelNodeId: string,
    propertyName: string,
    newType: ModelPropertyType,
    reference?: string
  ) => { success: boolean; removedEdgeIds?: string[] };
  updatePropertyName: (
    modelNodeId: string,
    oldPropertyName: string,
    newPropertyName: string
  ) => { success: boolean; error?: string };
  setModelNodeProperties: (modelNodeId: string, properties: ModelPropertyInterface[]) => boolean;

  // Edge Operations
  addEdge: (sourceId: string, targetId: string, propertyName?: string) => void;
  removeEdge: (edgeId: string) => void;
  removeEdgesBySourceHandle: (sourceId: string, sourceHandle: string) => void;

  // Bulk Operations
  bulkAddModelNodes: (models: ModelInterface[]) => ModelNodeInterface[];

  // Helpers
  findModelNodeByName: (name: string) => ModelNodeInterface | undefined;
  findModelNodeById: (id: string) => ModelNodeInterface | undefined;
  getModelsFromModelNodes: () => ModelInterface[];
  getState: () => ModelNodeStoreState;
}

export const useModelNodeStore = create<ModelNodeStoreState>()(
  immer((set, get) => ({
    modelNodes: [],
    edges: [],
    modelNodeColors: {},
    modelNodeById: new Map(),
    modelNodeByName: new Map(),
    edgeById: new Map(),
    propertiesByModelNode: new Map(),

    // ==================== MODELNODE OPERATIONS ====================

    addModelNode: (name, theme, position) => {
      const state = get();
      const newNodeId = `${state.modelNodes.length + 1}`;
      const nodeName = name || `New ModelInterface ${state.modelNodes.length + 1}`;
      const nodeTheme = theme || getThemeColor(state.modelNodes.length);

      const spacing = 600;
      const count = state.modelNodes.length;
      const angle = (2 * Math.PI * count) / Math.max(1, 8);
      const radius = spacing + count * 20;
      const centerX = 600;
      const centerY = 300;

      const nodePosition = position || {
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle),
      };

      const newNode: ModelNodeInterface = {
        id: newNodeId,
        type: "database_model",
        position: nodePosition,
        data: {
          id: newNodeId,
          name: nodeName,
          label: `${nodeName} Schema`,
          properties: [],
          theme: nodeTheme,
          isExpanded: true,

          addEdge: () => {},
          addModelProperty: (
            name: string,
            type: string,
            reference: string,
            createEdge = true
          ) => {
            get().addModelProperty(
              newNodeId,
              {
                name,
                type: type as ModelPropertyType,
                reference,
              },
              createEdge
            );
          },
          updateNodeName: (nodeId: string, name: string) => {
            get().updateModelNodeName(nodeId, name);
          },
          removeModelProperty: (propertyName: string) => {
            get().removeModelProperty(newNodeId, propertyName);
          },
          updateProperty: (nodeId: string, properties: ModelPropertyInterface[]) => {
            get().setModelNodeProperties(nodeId, properties);
          },
        },
      };

      set((state) => {
        state.modelNodes.push(newNode);
        state.modelNodeColors[newNodeId] = nodeTheme;
        state.modelNodeById.set(newNodeId, newNode);
        state.modelNodeByName.set(nodeName.toLowerCase(), newNode);
        state.propertiesByModelNode.set(newNodeId, new Set());
      });

      return newNode;
    },

    removeModelNode: (modelNodeId) => {
      set((state) => {
        const modelNode = state.modelNodeById.get(modelNodeId);
        if (!modelNode) return;

        const deletedModelName = modelNode.data.name;

        // CRITICAL FIX: Remove or update all references in other models that point to the deleted model
        // This ensures that if Model Y is deleted, all properties with reference: "Y" 
        // in other models (like Model X) are either removed or have their references cleared
        const removedPropertyNames: Array<{ nodeId: string; propertyName: string }> = [];

        state.modelNodes.forEach((otherNode) => {
          if (otherNode.id === modelNodeId) return; // Skip the deleted node itself

          let hasUpdatedProperties = false;
          const updatedProperties = otherNode.data.properties.filter((prop) => {
            // Remove properties that reference the deleted model
            if (
              prop.type === ModelPropertyType.Reference &&
              prop.reference === deletedModelName
            ) {
              hasUpdatedProperties = true;
              removedPropertyNames.push({
                nodeId: otherNode.id,
                propertyName: prop.name,
              });
              return false; // Filter out this property
            }
            return true;
          });

          // Only update if we found properties that needed updating
          if (hasUpdatedProperties) {
            const updatedOtherNode: ModelNodeInterface = {
              ...otherNode,
              data: {
                ...otherNode.data,
                properties: updatedProperties,
              },
            };

            // Update the other node in all maps and arrays
            state.modelNodeById.set(otherNode.id, updatedOtherNode);
            state.modelNodeByName.set(
              otherNode.data.name.toLowerCase(),
              updatedOtherNode
            );

            const otherNodeIndex = state.modelNodes.findIndex(
              (n) => n.id === otherNode.id
            );
            if (otherNodeIndex !== -1) {
              state.modelNodes[otherNodeIndex] = updatedOtherNode;
            }

            // Update properties set
            const propSet = new Set(updatedProperties.map((p) => p.name));
            state.propertiesByModelNode.set(otherNode.id, propSet);
          }
        });

        // Remove edges associated with the deleted properties
        state.edges = state.edges.filter((e) => {
          const shouldRemove = removedPropertyNames.some(
            (removed) =>
              e.source === removed.nodeId && e.sourceHandle === removed.propertyName
          );
          if (shouldRemove) {
            state.edgeById.delete(e.id);
          }
          return !shouldRemove;
        });

        // Remove the model node itself
        state.modelNodes = state.modelNodes.filter((n) => n.id !== modelNodeId);
        state.edges = state.edges.filter((e) => {
          const shouldRemove = e.source === modelNodeId || e.target === modelNodeId;
          if (shouldRemove) {
            state.edgeById.delete(e.id);
          }
          return !shouldRemove;
        });

        state.modelNodeById.delete(modelNodeId);
        state.modelNodeByName.delete(modelNode.data.name.toLowerCase());
        state.propertiesByModelNode.delete(modelNodeId);
        delete state.modelNodeColors[modelNodeId];
      });
    },

    updateModelNodeName: (modelNodeId, name) => {
      let result: { success: boolean; error?: string } = { success: false };

      set((state) => {
        const modelNode = state.modelNodeById.get(modelNodeId);
        if (!modelNode) {
          result = { success: false, error: "Model node not found" };
          return;
        }

        const sanitizedName = name
          .replace(/[^a-zA-Z0-9_ ]/g, " ")
          .split(/\s+/)
          .filter(Boolean)
          .map(
            (word) => word.charAt(0).toUpperCase() + word.slice(1)
          )
          .join("");

        if (!sanitizedName) {
          result = { success: false, error: "Model name cannot be empty" };
          return;
        }

        const lowerName = sanitizedName.toLowerCase();
        const existingModelNode = state.modelNodeByName.get(lowerName);

        // Check if model name already exists (excluding current model)
        if (existingModelNode && existingModelNode.id !== modelNodeId) {
          result = { success: false, error: `Model name "${sanitizedName}" already exists` };
          return;
        }

        // Check if model name matches any property name in this model
        const conflictingProperty = modelNode.data.properties.find(
          (p) => p.name.toLowerCase() === lowerName
        );

        if (conflictingProperty) {
          result = { success: false, error: "Model name cannot match a property name in this model" };
          return;
        }

        // Store old name for reference updates
        const oldName = modelNode.data.name;

        // Delete old name from map
        state.modelNodeByName.delete(modelNode.data.name.toLowerCase());

        // Create NEW node object to trigger Immer's tracking and ReactFlow re-render
        const updatedModelNode: ModelNodeInterface = {
          ...modelNode,
          data: {
            ...modelNode.data,
            name: sanitizedName,
            label: `${sanitizedName} Schema`,
          },
        };

        // Update maps with new node object
        state.modelNodeById.set(modelNodeId, updatedModelNode);
        state.modelNodeByName.set(lowerName, updatedModelNode);

        // Update the modelNodes array to trigger ReactFlow re-render
        const modelNodeIndex = state.modelNodes.findIndex((n) => n.id === modelNodeId);
        if (modelNodeIndex !== -1) {
          state.modelNodes[modelNodeIndex] = updatedModelNode;
        }

        // CRITICAL FIX: Update all references in other models that point to the old name
        // This ensures that if Model Y is renamed to Y1, all properties with reference: "Y" 
        // in other models (like Model X) are updated to reference: "Y1"
        state.modelNodes.forEach((otherNode) => {
          if (otherNode.id === modelNodeId) return; // Skip the renamed node itself

          let hasUpdatedProperties = false;
          const updatedProperties = otherNode.data.properties.map((prop) => {
            // Update reference if it points to the old model name
            if (
              prop.type === ModelPropertyType.Reference &&
              prop.reference === oldName
            ) {
              hasUpdatedProperties = true;
              return {
                ...prop,
                reference: sanitizedName,
              };
            }
            return prop;
          });

          // Only update if we found properties that needed updating
          if (hasUpdatedProperties) {
            const updatedOtherNode: ModelNodeInterface = {
              ...otherNode,
              data: {
                ...otherNode.data,
                properties: updatedProperties,
              },
            };

            // Update the other node in all maps and arrays
            state.modelNodeById.set(otherNode.id, updatedOtherNode);
            state.modelNodeByName.set(
              otherNode.data.name.toLowerCase(),
              updatedOtherNode
            );

            const otherNodeIndex = state.modelNodes.findIndex(
              (n) => n.id === otherNode.id
            );
            if (otherNodeIndex !== -1) {
              state.modelNodes[otherNodeIndex] = updatedOtherNode;
            }
          }
        });

        result = { success: true };
      });

      return result;
    },

    clearModelNodes: () => {
      set((state) => {
        state.modelNodes = [];
        state.edges = [];
        state.modelNodeColors = {};
        state.modelNodeById.clear();
        state.modelNodeByName.clear();
        state.edgeById.clear();
        state.propertiesByModelNode.clear();
      });
    },

    setModelNodeExpanded: (modelNodeId, isExpanded) => {
      let success = false;
      set((state) => {
        const modelNode = state.modelNodeById.get(modelNodeId);
        if (!modelNode) return;

        const updatedModelNode: ModelNodeInterface = {
          ...modelNode,
          data: {
            ...modelNode.data,
            isExpanded,
          },
        };

        state.modelNodeById.set(modelNodeId, updatedModelNode);
        state.modelNodeByName.set(modelNode.data.name.toLowerCase(), updatedModelNode);
        const modelNodeIndex = state.modelNodes.findIndex((n) => n.id === modelNodeId);
        if (modelNodeIndex !== -1) {
          state.modelNodes[modelNodeIndex] = updatedModelNode;
        }
        success = true;
      });
      return success;
    },

    // ==================== PROPERTY OPERATIONS ====================

    // Replace addModelProperty in nodeStore.ts with this:

    addModelProperty: (modelNodeId, property, createEdge = true) => {
      let success = false;

      set((state) => {
        const modelNode = state.modelNodeById.get(modelNodeId);

        if (!modelNode) {
          console.error(`ModelNodeInterface ${modelNodeId} not found`);
          return;
        }

        const propertySet = state.propertiesByModelNode.get(modelNodeId);
        if (propertySet?.has(property.name)) {
          console.error(
            `ModelPropertyInterface ${property.name} already exists in ${modelNode.data.name}`
          );
          return;
        }

        // Create new properties array
        const updatedProperties = [...modelNode.data.properties, property];

        // CRITICAL: Create a NEW node object to trigger Immer's tracking
        // This is necessary because Immer wraps objects in Proxies,
        // and simply mutating the existing object doesn't always propagate
        const updatedModelNode: ModelNodeInterface = {
          ...modelNode,
          data: {
            ...modelNode.data,
            properties: updatedProperties,
          },
        };

        // Now update the maps with the NEW node object
        state.modelNodeById.set(modelNodeId, updatedModelNode);
        state.modelNodeByName.set(modelNode.data.name.toLowerCase(), updatedModelNode);

        // Also update the modelNodes array
        const modelNodeIndex = state.modelNodes.findIndex((n) => n.id === modelNodeId);
        if (modelNodeIndex !== -1) {
          state.modelNodes[modelNodeIndex] = updatedModelNode;
        }

        state.propertiesByModelNode.get(modelNodeId)?.add(property.name);
        success = true;

        // Create edge if needed
        if (
          createEdge &&
          property.type === ModelPropertyType.Reference &&
          property.reference
        ) {
          const targetModelNode = state.modelNodeByName.get(
            property.reference.toLowerCase()
          );

          if (targetModelNode) {
            const propertyId = `${modelNodeId}-${property.name}`;
            const edge = buildEdgeFromIds(
              modelNodeId,
              targetModelNode.id,
              propertyId,
              `${targetModelNode.id}-target-center`,
              state.modelNodes,
              PROFESSIONAL_BACKGROUND_COLORS,
              state.modelNodeColors
            );

            state.edges.push(edge);
            state.edgeById.set(edge.id, edge);
          }
        }
      });

      return success;
    },

    // Apply same pattern to removeModelProperty:
    removeModelProperty: (modelNodeId, propertyName) => {
      let success = false;

      set((state) => {
        const modelNode = state.modelNodeById.get(modelNodeId);

        if (!modelNode) return;

        const property = modelNode.data.properties.find(
          (p) => p.name === propertyName
        );

        const updatedProperties = modelNode.data.properties.filter(
          (p) => p.name !== propertyName
        );

        // Create NEW node object
        const updatedModelNode: ModelNodeInterface = {
          ...modelNode,
          data: {
            ...modelNode.data,
            properties: updatedProperties,
          },
        };

        state.modelNodeById.set(modelNodeId, updatedModelNode);
        state.modelNodeByName.set(modelNode.data.name.toLowerCase(), updatedModelNode);

        const modelNodeIndex = state.modelNodes.findIndex((n) => n.id === modelNodeId);
        if (modelNodeIndex !== -1) {
          state.modelNodes[modelNodeIndex] = updatedModelNode;
        }

        state.propertiesByModelNode.get(modelNodeId)?.delete(propertyName);
        success = true;

        // Remove associated edge if it was a reference
        if (property?.type === ModelPropertyType.Reference) {
          state.edges = state.edges.filter((e) => {
            const shouldRemove =
              e.source === modelNodeId && e.sourceHandle === propertyName;
            if (shouldRemove) {
              state.edgeById.delete(e.id);
            }
            return !shouldRemove;
          });
        }
      });

      return success;
    },

    // And updateProperty:
    updateProperty: (modelNodeId, propertyName, updates) => {
      let success = false;

      set((state) => {
        const modelNode = state.modelNodeById.get(modelNodeId);

        if (!modelNode) return;

        const property = modelNode.data.properties.find(
          (p) => p.name === propertyName
        );

        if (property) {
          const updatedProperty = { ...property, ...updates };
          const updatedProperties = modelNode.data.properties.map((p) =>
            p.name === propertyName ? updatedProperty : p
          );

          // Create NEW node object
          const updatedModelNode: ModelNodeInterface = {
            ...modelNode,
            data: {
              ...modelNode.data,
              properties: updatedProperties,
            },
          };

          state.modelNodeById.set(modelNodeId, updatedModelNode);
          state.modelNodeByName.set(modelNode.data.name.toLowerCase(), updatedModelNode);

          const modelNodeIndex = state.modelNodes.findIndex((n) => n.id === modelNodeId);
          if (modelNodeIndex !== -1) {
            state.modelNodes[modelNodeIndex] = updatedModelNode;
          }

          success = true;
        }
      });

      return success;
    },

    // Update property type with edge management
    updatePropertyType: (modelNodeId, propertyName, newType, reference) => {
      console.log("updatePropertyType", modelNodeId, propertyName, newType, reference);
      let result: { success: boolean; removedEdgeIds?: string[] } = { success: false };
      const removedEdgeIds: string[] = [];

      set((state) => {
        const modelNode = state.modelNodeById.get(modelNodeId);

        if (!modelNode) {
          result = { success: false };
          return;
        }

        const property = modelNode.data.properties.find(
          (p) => p.name === propertyName
        );

        if (!property) {
          result = { success: false };
          return;
        }

        const wasReference = property.type === ModelPropertyType.Reference;
        const isNowReference = newType === ModelPropertyType.Reference;

        // Remove edge if changing FROM Reference type
        if (wasReference && !isNowReference) {
          // Edge sourceHandle format is: `${modelNodeId}-${propertyName}`
          const expectedSourceHandle = `${modelNodeId}-${propertyName}`;
          
          console.log("Looking for edges to remove:", {
            modelNodeId,
            propertyName,
            expectedSourceHandle,
            allEdges: state.edges.map(e => ({
              id: e.id,
              source: e.source,
              sourceHandle: e.sourceHandle
            }))
          });
          
          state.edges = state.edges.filter((e) => {
            const shouldRemove =
              e.source === modelNodeId && e.sourceHandle === expectedSourceHandle;
            if (shouldRemove) {
              console.log("Removing edge:", e.id);
              removedEdgeIds.push(e.id);
              state.edgeById.delete(e.id);
            }
            return !shouldRemove;
          });
          
          console.log("Removed edge IDs:", removedEdgeIds);
        }

        // Update property
        const updatedProperty: ModelPropertyInterface = {
          ...property,
          type: newType,
          reference: isNowReference ? reference : undefined,
        };

        const updatedProperties = modelNode.data.properties.map((p) =>
          p.name === propertyName ? updatedProperty : p
        );

        // Create NEW node object
        const updatedModelNode: ModelNodeInterface = {
          ...modelNode,
          data: {
            ...modelNode.data,
            properties: updatedProperties,
          },
        };

        state.modelNodeById.set(modelNodeId, updatedModelNode);
        state.modelNodeByName.set(modelNode.data.name.toLowerCase(), updatedModelNode);

        const modelNodeIndex = state.modelNodes.findIndex((n) => n.id === modelNodeId);
        if (modelNodeIndex !== -1) {
          state.modelNodes[modelNodeIndex] = updatedModelNode;
        }

        // Add edge if changing TO Reference type
        if (!wasReference && isNowReference && reference) {
          const targetModelNode = state.modelNodeByName.get(reference.toLowerCase());

          if (targetModelNode) {
            const propertyId = `${modelNodeId}-${propertyName}`;
            const edge = buildEdgeFromIds(
              modelNodeId,
              targetModelNode.id,
              propertyId,
              `${targetModelNode.id}-target-center`,
              state.modelNodes,
              PROFESSIONAL_BACKGROUND_COLORS,
              state.modelNodeColors
            );

            // Check if edge already exists
            if (!state.edgeById.has(edge.id)) {
              state.edges.push(edge);
              state.edgeById.set(edge.id, edge);
            }
          }
        }

        result = { success: true, removedEdgeIds: removedEdgeIds.length > 0 ? removedEdgeIds : undefined };
      });

      return result;
    },

    // Update property name with validation
    updatePropertyName: (modelNodeId, oldPropertyName, newPropertyName) => {
      // Sanitize the new property name
      let sanitizedName = newPropertyName
        .replace(/\s+/g, '')
        .replace(/[^a-zA-Z0-9_$]/g, '')
        .replace(/^[^a-zA-Z_$]+/, '');

      if (!sanitizedName) {
        return { success: false, error: "Property name cannot be empty" };
      }

      let result: { success: boolean; error?: string } = { success: false };

      set((state) => {
        const modelNode = state.modelNodeById.get(modelNodeId);
        if (!modelNode) {
          result = { success: false, error: "Model node not found" };
          return;
        }

        // Check if property name matches model name (case-insensitive)
        if (modelNode.data.name.toLowerCase() === sanitizedName.toLowerCase()) {
          result = { success: false, error: "Property name cannot match model name" };
          return;
        }

        // Check if property name already exists (excluding the current property being renamed)
        const existingProperty = modelNode.data.properties.find(
          (p) => p.name.toLowerCase() === sanitizedName.toLowerCase() && p.name !== oldPropertyName
        );

        if (existingProperty) {
          result = { success: false, error: "Property name already exists in this model" };
          return;
        }

        // Find the property to rename
        const property = modelNode.data.properties.find(
          (p) => p.name === oldPropertyName
        );

        if (!property) {
          result = { success: false, error: "Property not found" };
          return;
        }

        // Update property name
        const updatedProperty = { ...property, name: sanitizedName };
        const updatedProperties = modelNode.data.properties.map((p) =>
          p.name === oldPropertyName ? updatedProperty : p
        );

        // Create NEW node object
        const updatedModelNode: ModelNodeInterface = {
          ...modelNode,
          data: {
            ...modelNode.data,
            properties: updatedProperties,
          },
        };

        state.modelNodeById.set(modelNodeId, updatedModelNode);
        state.modelNodeByName.set(modelNode.data.name.toLowerCase(), updatedModelNode);

        const modelNodeIndex = state.modelNodes.findIndex((n) => n.id === modelNodeId);
        if (modelNodeIndex !== -1) {
          state.modelNodes[modelNodeIndex] = updatedModelNode;
        }

        // Update properties set
        state.propertiesByModelNode.get(modelNodeId)?.delete(oldPropertyName);
        state.propertiesByModelNode.get(modelNodeId)?.add(sanitizedName);

        // Update edge sourceHandle if this property has an edge
        if (property.type === ModelPropertyType.Reference) {
          state.edges.forEach((edge) => {
            if (edge.source === modelNodeId && edge.sourceHandle === oldPropertyName) {
              edge.sourceHandle = sanitizedName;
              // Update edge ID to match new property name
              const newEdgeId = `${modelNodeId}-${sanitizedName}`;
              state.edgeById.delete(edge.id);
              edge.id = newEdgeId;
              state.edgeById.set(newEdgeId, edge);
            }
          });
        }

        result = { success: true };
      });

      return result;
    },

    // And setModelNodeProperties:
    setModelNodeProperties: (modelNodeId, properties) => {
      set((state) => {
        const modelNode = state.modelNodeById.get(modelNodeId);

        if (modelNode) {
          // Create NEW node object
          const updatedModelNode: ModelNodeInterface = {
            ...modelNode,
            data: {
              ...modelNode.data,
              properties: properties,
            },
          };

          state.modelNodeById.set(modelNodeId, updatedModelNode);
          state.modelNodeByName.set(modelNode.data.name.toLowerCase(), updatedModelNode);

          const modelNodeIndex = state.modelNodes.findIndex((n) => n.id === modelNodeId);
          if (modelNodeIndex !== -1) {
            state.modelNodes[modelNodeIndex] = updatedModelNode;
          }

          const propSet = new Set(properties.map((p) => p.name));
          state.propertiesByModelNode.set(modelNodeId, propSet);
        }
      });
      return true;
    },

    // ==================== EDGE OPERATIONS ====================

    addEdge: (sourceId, targetId, propertyName) => {
      const state = get();
      const propertyId = `${sourceId}-${propertyName || "connection"}`;

      const edge = buildEdgeFromIds(
        sourceId,
        targetId,
        propertyId,
        `${targetId}-target-center`,
        state.modelNodes,
        PROFESSIONAL_BACKGROUND_COLORS,
        state.modelNodeColors
      );

      set((state) => {
        if (state.edgeById.has(edge.id)) {
          state.edges = state.edges.filter((e) => e.id !== edge.id);
        }

        state.edges.push(edge);
        state.edgeById.set(edge.id, edge);
      });
    },

    removeEdge: (edgeId) => {
      set((state) => {
        state.edges = state.edges.filter((e) => e.id !== edgeId);
        state.edgeById.delete(edgeId);
      });
    },

    removeEdgesBySourceHandle: (sourceId, sourceHandle) => {
      set((state) => {
        state.edges = state.edges.filter((e) => {
          const shouldRemove =
            e.source === sourceId && e.sourceHandle === sourceHandle;
          if (shouldRemove) {
            state.edgeById.delete(e.id);
          }
          return !shouldRemove;
        });
      });
    },

    // ==================== BULK OPERATIONS ====================

    bulkAddModelNodes: (models) => {
      const state = get();
      const positions = ensureModelPositions(models);
      const createdModelNodes: ModelNodeInterface[] = [];

      set((state) => {
        const colorMapForBulk: Record<string, string> = {
          ...state.modelNodeColors,
        };

        // Phase 1: Create all modelNodes with properties
        models.forEach((model, index) => {
          const currentModelNodeCount = state.modelNodes.length;
          const theme = getThemeColor(currentModelNodeCount);
          const position = positions[model.name];
          const modelNodeId = model.name || `${index}`;

          colorMapForBulk[modelNodeId] = theme;

          const newModelNode: ModelNodeInterface = {
            id: modelNodeId,
            type: "database_model",
            position: position,
            data: {
              id: modelNodeId,
              name: model.name,
              label: `${model.name} Schema`,
              properties: model.properties,
              theme: theme,
              isExpanded: model.isExpanded ?? true,

              addEdge: () => {},
              addModelProperty: (
                name: string,
                type: string,
                reference: string,
                createEdge = true
              ) => {
                get().addModelProperty(
                  modelNodeId,
                  {
                    name,
                    type: type as ModelPropertyType,
                    reference,
                  },
                  createEdge
                );
              },
              updateNodeName: (nodeId: string, name: string) => {
                get().updateModelNodeName(nodeId, name);
              },
              removeModelProperty: (propertyName: string) => {
                get().removeModelProperty(modelNodeId, propertyName);
              },
              updateProperty: (nodeId: string, properties: ModelPropertyInterface[]) => {
                get().setModelNodeProperties(nodeId, properties);
              },
            },
          };

          state.modelNodes.push(newModelNode);
          state.modelNodeById.set(modelNodeId, newModelNode);
          state.modelNodeByName.set(model.name.toLowerCase(), newModelNode);
          state.modelNodeColors[modelNodeId] = theme;

          const propSet = new Set(model.properties.map((p) => p.name));
          state.propertiesByModelNode.set(modelNodeId, propSet);

          createdModelNodes.push(newModelNode);
        });

        // Phase 2: Create all edges
        models.forEach((model, index) => {
          const modelNode = createdModelNodes[index];

          model.properties.forEach((property) => {
            if (
              property.type === ModelPropertyType.Reference &&
              property.reference
            ) {
              const targetModelNode = state.modelNodeByName.get(
                property.reference.toLowerCase()
              );

              if (targetModelNode?.id) {
                const propertyId = `${modelNode.id}-${property.name}`;

                const edge = buildEdgeFromIds(
                  modelNode.id,
                  targetModelNode.id,
                  propertyId,
                  `${targetModelNode.id}-target-center`,
                  state.modelNodes,
                  PROFESSIONAL_BACKGROUND_COLORS,
                  colorMapForBulk
                );

                state.edges.push(edge);
                state.edgeById.set(edge.id, edge);
              }
            }
          });
        });
      });

      return createdModelNodes;
    },

    // ==================== HELPERS ====================

    findModelNodeByName: (name) => {
      return get().modelNodeByName.get(name.toLowerCase());
    },

    findModelNodeById: (id) => {
      return get().modelNodeById.get(id);
    },

    getModelsFromModelNodes: () => {
      return get().modelNodes.map((modelNode) => ({
        name: modelNode.data.name,
        properties: modelNode.data.properties,
        position: modelNode.position,
        isExpanded: modelNode.data.isExpanded,
        options: { timestamps: true },
      }));
    },

    getState: () => {
      return get();
    },
  }))
);

function getThemeColor(index: number): string {
  const colors = PROFESSIONAL_BACKGROUND_COLORS;
  return colors[index % colors.length];
}
